import { useMemo } from 'react'
import { useState } from 'react'
import cloneDeep from 'lodash/cloneDeep'

import useRegionsListQuery from 'common/api/queries/useRegionsListQuery'
import AvatarList from 'common/components/AvatarList'
import { Column } from 'common/components/table'
import BaseCell from 'common/components/table/cells/Base'
import TableWidget from 'common/components/widget/tableWidget'
import { formatPastDate } from 'common/helpers/date'
import { LanguageRule } from 'features/admin/language/api/api'

import EditLanguageRulesDrawer from '../languageRulesForm/EditLanguageRulesDrawer'

import LanguageRulesActions from './LanguageRulesActions'

type Props = {
  languageRules?: LanguageRule[]
}

const LanguageRulesList = ({ languageRules }: Props) => {
  const [selectedRule, setSelectedRule] = useState<{
    id: string | undefined
    isVisible: boolean
  }>({
    id: undefined,
    isVisible: false,
  })

  const { data: regions } = useRegionsListQuery()

  const columns: Column<LanguageRule>[] = useMemo(
    () => [
      {
        Header: 'Rule',
        accessor: 'rule',
      },
      {
        Header: 'ISO Language',
        accessor: 'isoLanguage',
        width: 60,
        Cell: ({ value }) => {
          const region = regions?.find(
            (region) => region.language_settings?.iso_language === value
          )

          const isoLanguage = value === 'all' ? 'All' : (region?.name ?? '')

          return (
            <BaseCell className="text-gold-700 text-base">
              {isoLanguage}
            </BaseCell>
          )
        },
      },
      {
        Header: 'Last Modified',
        minWidth: 60,
        width: 60,
        accessor: 'lastUpdated',
        Cell: ({ value }) => (
          <BaseCell className="text-gold-700 text-base">
            {formatPastDate(value)}
          </BaseCell>
        ),
      },
      {
        Header: '',
        accessor: 'lastUpdatedByName',
        width: 20,
        disableSortBy: true,
        align: 'right',
        Cell: ({ value }) => {
          return (
            <BaseCell className="flex justify-center">
              {value !== undefined ? <AvatarList userNames={[value]} /> : '  '}
            </BaseCell>
          )
        },
      },
      {
        Header: '',
        id: 'actions',
        disableSortBy: true,
        accessor: 'id',
        align: 'right',
        disableFilters: true,
        width: 40,
        Cell: ({ row }) => (
          <BaseCell className="invisible group-hover:visible">
            <LanguageRulesActions
              ruleId={row.original.id}
              onEdit={() => {
                setSelectedRule({
                  id: row.original.id,
                  isVisible: true,
                })
              }}
            />
          </BaseCell>
        ),
      },
    ],
    [regions]
  )
  const selectedLanguageRule = cloneDeep(languageRules)?.find(
    ({ id }) => selectedRule.id === id
  )
  return (
    <>
      <TableWidget.Widget
        columns={columns}
        data={languageRules ?? []}
        firstUseText="Nothing to see here… There is no content to show."
        initialState={{
          sortBy: [
            {
              id: 'lastUpdated',
              desc: true,
            },
          ],
        }}
      />
      <EditLanguageRulesDrawer
        isOpen={selectedRule.isVisible}
        onOpenChange={() => {
          setSelectedRule(({ id }) => {
            return {
              id,
              isVisible: false,
            }
          })
        }}
        languageRule={selectedLanguageRule}
      />
    </>
  )
}

export default LanguageRulesList
